import React, { useEffect } from 'react';
import {
  Route,
  createHashRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import About from './pages/About';
import NiKendo from './pages/NiKendo';
import Workspace from './components/Layout/Workspace/Workspace';
// import NotFound from './pages/NotFound';
import { useAuth } from 'react-oidc-context';
import { NiKendoProvider } from './context/NiKendoContext';
import WebFont from 'webfontloader';
import Loader from 'components/Common/Loader';
import Unauthorized from 'pages/SessionExpired';
import SessionTimeout from 'components/Common/SessionTimeout';
import { useSelector } from 'react-redux';
import { RootState } from 'types/types';
import DashboardCreator from 'components/Dashboard/DashboardCreator';
import ProtectedRoute from 'components/Common/ProtectedRoute';
import NotFound from 'pages/NotFound';
import LandingPage from 'pages/LandingPage';
import LandingDashboards from 'pages/LandingDashboards';
import MapComponent from 'pages/MapComponent';
import localforage from 'localforage';
import AlarmManagement from 'components/AlarmManagement/AlarmManagement';
import CreateForm from 'components/AlarmManagement/components/CreateForm/CreateForm';
import AIAssistant from 'components/AIAssistant/AIAssistant';

function App() {
  const auth = useAuth();
  const sessionTimeout = useSelector(
    (state: RootState) =>
      state.applicationState.applicationSettings.SESSION_TIMEOUT
  );

  useEffect(() => {
    switch (auth.activeNavigator) {
      case 'signinSilent':
        // add loader
        return;
      case 'signoutRedirect':
        // add loader
        return;
      default:
        if (auth.isLoading) {
          // add loader
          return;
        } else if (auth.error) {
          // add loader
          return;
        } else {
          // add loader
        }
    }
    if (!auth.isAuthenticated) {
      auth.signinRedirect({
        state: {
          originalUrlHash: window.location.hash || '#/view/landingPageView',
        },
      });
    } else {
      // Redirect to base URL when authenticated and not already on the base URL
      if (window.location.search !== '') {
        const state: any = auth.user?.state;
        window.location.href =
          window.location.origin + '/' + state.originalUrlHash;
      }
    }
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth, auth.signinSilent]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localforage
        .clear()
        .then(() => {
          console.log('localforage cleared');
        })
        .catch((error) => {
          console.error('Error clearing localforage:', error);
        });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSessionExpired = () => {
    auth.signoutRedirect();
  };

  const router = createHashRouter(
    createRoutesFromElements(
      <>
        <Route
          path="/notFound"
          element={
            <NiKendoProvider>
              <Workspace>
                <NotFound />
              </Workspace>
            </NiKendoProvider>
          }
        />
        <Route
          path="/about"
          element={
            <NiKendoProvider>
              <Workspace>
                <About />
              </Workspace>
            </NiKendoProvider>
          }
        />
        <Route
          path="/sessionExpired"
          element={
            <NiKendoProvider>
              <Workspace>
                <Unauthorized />
              </Workspace>
            </NiKendoProvider>
          }
        />
        <Route
          path="/view/alarm-management-view"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route
            path="/view/alarm-management-view"
            element={<AlarmManagement />}
          />
        </Route>
        <Route
          path="/alarm-management/new"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/alarm-management/new" element={<CreateForm />} />
        </Route>
        <Route
          path="/alarm-management/edit/:id"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/alarm-management/edit/:id" element={<CreateForm />} />
        </Route>
        <Route
          path="/alarm-management/duplicate"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/alarm-management/duplicate" element={<CreateForm />} />
        </Route>
        <Route
          path="/dashboard/ni-ran-advanced-map-dashboard"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route
            path="/dashboard/ni-ran-advanced-map-dashboard"
            element={<MapComponent />}
          />
        </Route>
        <Route
          path="/dashboard/:dashboardId"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route
            path="/dashboard/:dashboardId"
            element={
              <>
                <DashboardCreator />
              </>
            }
          />
        </Route>
        <Route
          path="/view/landingPageView"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/view/landingPageView" element={<LandingPage />} />
        </Route>
        <Route
          path="/view/landingDashboardsView"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route
            path="/view/landingDashboardsView"
            element={<LandingDashboards />}
          />
        </Route>
        <Route
          path="/view/:viewId"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/view/:viewId" element={<NiKendo />} />
        </Route>
        <Route
          path="/ai-assistant"
          element={
            <NiKendoProvider>
              <Workspace>
                <AIAssistant newTab={true} />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/ai-assistant" element={<NiKendo />} />
        </Route>
        <Route
          path="/dynamicView/:viewId"
          element={
            <NiKendoProvider>
              <Workspace>
                <ProtectedRoute />
              </Workspace>
            </NiKendoProvider>
          }
        >
          <Route path="/dynamicView/:viewId" element={<NiKendo />} />
        </Route>
        <Route
          path="/"
          element={
            <NiKendoProvider>
              <Workspace>
                <LandingPage></LandingPage>
              </Workspace>
            </NiKendoProvider>
          }
        ></Route>
      </>
    )
  );

  if (auth.isAuthenticated) {
    return (
      <div className="root-container">
        {/* required for kendo ni bundle */}
        <div id="appLayout" style={{ display: 'none' }}></div>
        {/* seesion timeout */}
        <SessionTimeout
          timeSpan={parseInt(sessionTimeout)}
          onSessionExpired={handleSessionExpired}
        />
        <RouterProvider router={router} />
      </div>
    );
  } else {
    return <Loader />;
  }
}

export default App;
